import { useCallback, useEffect, useRef } from "react";
import { useSectionContext } from "../hooks/useSectionContext";
import PersonalInfo from "./sections/PersonalInfo/PersonalInfo";
import Settings from "./sections/Settings";
import { EmptySectionPlaceholder } from "./sections/EmptySectionPlaceholder";
import Documents from "./sections/Documents/Documents";
import type { V2Member } from "../../../apis/userApi";
import type { SectionId, UsersInfoType } from "../types";
import Plans from "./sections/Plans/Plans";
import Billing from "./sections/Billing/Billing";
import Communications from "./sections/Communications/Communications";
import Notes from "./sections/Notes/Notes";
import Summary from "./sections/Summary";

type ProfileSectionsProps = {
  userUuid: string;
  profileData: V2Member;
  isDataLoading: boolean;
  usersInfo: UsersInfoType;
  isUsersInfoFetching: boolean;
  isProfileError: boolean;
};

export default function ProfileSections({
  userUuid,
  profileData,
  isDataLoading,
  usersInfo,
  isUsersInfoFetching,
  isProfileError,
}: ProfileSectionsProps) {
  const commonClass = "tw-py-6 tw-px-6";
  const { setActiveSection } = useSectionContext();

  const sectionsRefs = useRef<Map<SectionId, HTMLDivElement>>(new Map());

  const registerSectionRef = useCallback(
    (id: SectionId) => (ref: HTMLDivElement | null) => {
      if (ref) {
        sectionsRefs.current.set(id, ref);
      } else {
        sectionsRefs.current.delete(id);
      }
    },
    [],
  );

  // Detect which section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id as SectionId);
          }
        });
      },
      {
        rootMargin: "-30% 0px -35% 0px",
        threshold: 0.1,
        /*
        Defines the viewport: top/right/bottom/left
        Threshold: section is considered "intersecting"
        when at least x% is visible within the viewport.
        */
      },
    );

    Array.from(sectionsRefs.current.values()).forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [setActiveSection]);

  return (
    <div className="tw-flex-1">
      <div
        id="summary"
        ref={registerSectionRef("summary")}
        className="tw-px-6 tw-pt-4 tw-pb-6"
      >
        <Summary />
      </div>
      <div id="plans" ref={registerSectionRef("plans")} className={commonClass}>
        <Plans userUuid={userUuid} />
      </div>
      <div
        id="billing"
        ref={registerSectionRef("billing")}
        className={commonClass}
      >
        <Billing userUuid={userUuid} />
      </div>
      <div
        id="schedule"
        ref={registerSectionRef("schedule")}
        className={commonClass}
      >
        <EmptySectionPlaceholder title="Schedule" />
      </div>
      <div
        id="communication"
        ref={registerSectionRef("communication")}
        className={commonClass}
      >
        <Communications userUuid={userUuid} />
      </div>
      <div
        id="personal-info"
        ref={registerSectionRef("personal-info")}
        className={commonClass}
      >
        <PersonalInfo
          profileData={profileData}
          isDataLoading={isDataLoading}
          userUuid={userUuid}
          usersInfo={usersInfo}
          isUsersInfoFetching={isUsersInfoFetching}
          isProfileError={isProfileError}
        />
      </div>
      <div id="notes" ref={registerSectionRef("notes")} className={commonClass}>
        <Notes userUuid={userUuid} />
      </div>
      <div
        id="documents"
        ref={registerSectionRef("documents")}
        className={commonClass}
      >
        <Documents userUuid={userUuid}/>
      </div>
      <div
        id="settings"
        ref={registerSectionRef("settings")}
        className={commonClass}
      >
        <Settings
          userUuid={userUuid}
          profileData={profileData}
          isDataLoading={isDataLoading}
        />
      </div>
    </div>
  );
}
